/**
 * useJobRunCounts — fresh, realtime URL-progress counts for a single job run.
 *
 * Backstory: `job_runs.urls_discovered` / `urls_scraped` are cached counter
 * columns only updated by the slow Supabase cron-based discover-worker. When
 * a job runs through the Firebase Cloud Functions path (which is now the
 * canonical fast path), those columns lag the actual progress by minutes.
 * The UI looked frozen even when workers were finding hundreds of URLs/min.
 *
 * This hook derives the counts from `scrape_queue` row counts directly —
 * always fresh, no worker has to remember to bump a counter. We subscribe
 * to Supabase Realtime INSERT/UPDATE events filtered by `job_run_id` and
 * refetch on every change; an initial fetch covers the page-load case.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchJobRunCounts } from "@/lib/api";
import type { QueueStats } from "@/lib/api/queue";
import { supabase } from "@/integrations/supabase/client";

interface UseJobRunCountsResult extends QueueStats {
  isLoading: boolean;
  refetch: () => void;
}

const EMPTY: QueueStats = { total: 0, pending: 0, in_progress: 0, completed: 0, failed: 0 };

export function useJobRunCounts(jobRunId: string | null | undefined): UseJobRunCountsResult {
  const [counts, setCounts] = useState<QueueStats>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  // Debounce realtime bursts: scrape_queue can fire dozens of UPDATEs/sec
  // when many workers complete in parallel. Coalesce into a single fetch.
  const pendingFetchRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!jobRunId) {
      setCounts(EMPTY);
      return;
    }
    setIsLoading(true);
    try {
      const next = await fetchJobRunCounts(jobRunId);
      setCounts(next);
    } catch (err) {
      console.error("useJobRunCounts: failed to fetch", err);
    } finally {
      setIsLoading(false);
    }
  }, [jobRunId]);

  const scheduleFetch = useCallback(() => {
    if (pendingFetchRef.current !== null) return;
    pendingFetchRef.current = window.setTimeout(() => {
      pendingFetchRef.current = null;
      void load();
    }, 400);
  }, [load]);

  useEffect(() => {
    if (!jobRunId) {
      setCounts(EMPTY);
      return;
    }
    void load();

    const channel = supabase
      .channel(`scrape_queue:run:${jobRunId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "scrape_queue",
          filter: `job_run_id=eq.${jobRunId}`,
        },
        scheduleFetch,
      )
      .subscribe();

    return () => {
      if (pendingFetchRef.current !== null) {
        window.clearTimeout(pendingFetchRef.current);
        pendingFetchRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [jobRunId, load, scheduleFetch]);

  return { ...counts, isLoading, refetch: load };
}
